import React from 'react';
import { TrendingUp, Users, Database, AlertCircle, UserCheck, Calendar, Briefcase, Mail } from 'lucide-react';
import Card from '../ui/Card';

const OverviewTab = ({ stats, appSettings, loading, error, appSettingsLoading }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Ensure backend timestamps are parsed as UTC by adding 'Z' when missing
      let utcDateString = dateString;
      if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 19)) {
        utcDateString = dateString + 'Z';
      }
      const date = new Date(utcDateString);
      const timezone = appSettings?.timezone || 'UTC';
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (e) {
      console.error('formatDateTime error:', e, 'for dateString:', dateString);
      return 'Invalid Date';
    }
  };

  if (loading || appSettingsLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#6b7280' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #e5e7eb', borderTop: '3px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '12px', fontSize: '16px' }}>Loading statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', color: '#dc2626' }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  // Capability-aware behavior
  const capabilities = stats?.capabilities || [];
  const totals = stats?.totals || {
    total_users: stats?.total_users,
    total_jobs_completed: stats?.total_jobs_completed,
    total_contacts_fetched: stats?.total_contacts_fetched,
  };

  const hasStats = typeof totals?.total_users === 'number' || typeof totals?.total_jobs_completed === 'number' || typeof totals?.total_contacts_fetched === 'number';
  const canViewRecentErrors = capabilities.includes('dashboard.view_recent_errors') && Array.isArray(stats?.recent_errors);

  if (!hasStats && !Array.isArray(stats?.recent_users) && !canViewRecentErrors) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Card.Content>
            <p style={{ color: '#6b7280' }}>No dashboard data available for your role.</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  const metricCards = [];
  if (hasStats) {
    metricCards.push(
      {
        title: 'Total Users',
        value: totals?.total_users || 0,
        icon: Users,
        color: '#3b82f6',
        bgColor: '#eff6ff'
      },
      {
        title: 'Jobs Completed',
        value: totals?.total_jobs_completed || 0,
        icon: TrendingUp,
        color: '#10b981',
        bgColor: '#f0fdf4'
      },
      {
        title: 'Contacts Fetched',
        value: totals?.total_contacts_fetched || 0,
        icon: Database,
        color: '#f59e0b',
        bgColor: '#fffbeb'
      },
    );
  }
  if (canViewRecentErrors) {
    metricCards.push({
      title: 'Recent Errors',
      value: stats?.recent_errors?.length || 0,
      icon: AlertCircle,
      color: '#ef4444',
      bgColor: '#fef2f2'
    });
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Metrics Grid */}
      {metricCards.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          {metricCards.map((metric, index) => (
            <Card key={index} style={{ background: `linear-gradient(135deg, ${metric.bgColor} 0%, white 100%)`, border: `1px solid ${metric.color}20`, transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>{metric.title}</h3>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>{Number(metric.value || 0).toLocaleString()}</p>
                </div>
                <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: metric.bgColor }}>
                  <metric.icon size={24} color={metric.color} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Recent Activity Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        {/* Recent Users */}
        {Array.isArray(stats?.recent_users) && (
          <Card>
            <Card.Header>
              <Card.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserCheck size={20} color="#667eea" />
                Recent Users Logged In
              </Card.Title>
            </Card.Header>
            <Card.Content>
              {stats.recent_users.length > 0 ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {stats.recent_users.map((user, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', padding: '12px', borderBottom: index < stats.recent_users.length - 1 ? '1px solid #e5e7eb' : 'none', borderRadius: '6px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', marginRight: '12px' }}>
                        {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: '600', color: '#111827', margin: 0, fontSize: '14px' }}>{user.full_name || 'N/A'}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Mail size={12} />
                            {user.email}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Briefcase size={12} />
                            {user.company_name || 'N/A'}
                          </div>
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={10} />
                          Last login: {formatDateTime(user.last_login_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>No recent user activity</p>
              )}
            </Card.Content>
          </Card>
        )}

        {/* Recent Errors */}
        {canViewRecentErrors && (
          <Card>
            <Card.Header>
              <Card.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={20} color="#ef4444" />
                Recent Server Errors
              </Card.Title>
            </Card.Header>
            <Card.Content>
              {stats?.recent_errors?.length > 0 ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {stats.recent_errors.map((error, index) => (
                    <div key={index} style={{ padding: '12px', borderBottom: index < stats.recent_errors.length - 1 ? '1px solid #e5e7eb' : 'none', borderLeft: '3px solid #ef4444', backgroundColor: '#fef2f2', borderRadius: '0 6px 6px 0', marginBottom: '8px' }}>
                      <p style={{ fontWeight: '600', color: '#dc2626', margin: 0, fontSize: '13px', marginBottom: '4px' }}>Job ID: {error.job_id}</p>
                      <p style={{ color: '#374151', margin: 0, fontSize: '12px', marginBottom: '4px' }}>User: {error.user_email}</p>
                      <p style={{ color: '#6b7280', margin: 0, fontSize: '11px', marginBottom: '4px' }}>Error: {error.error_message}</p>
                      <p style={{ color: '#9ca3af', margin: 0, fontSize: '10px' }}>{formatDateTime(error.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#10b981', textAlign: 'center', padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                  ✅ No recent errors - System running smoothly!
                </p>
              )}
            </Card.Content>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;